import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { getFileUrl } from "@shared/utils/fileUrl";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/components/ui/tooltip";
import { AspectRatio } from "@shared/components/ui/aspect-ratio";

const ImageViewer = ({
  path,
  label,
  alt,
  ratio = 16 / 12,
  showLabel = true,
  overlay = true,
  tooltipText,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation();

  const fullPath = path ? getFileUrl(path) : null;

  const imageContent = (
    <div className="relative overflow-hidden rounded-md border bg-muted/20 hover:bg-muted/30 transition-colors">
      {!fullPath ? (
        <AspectRatio
          ratio={ratio}
          className="flex items-center justify-center text-muted-foreground"
        >
          {t("common.noDocument")}
        </AspectRatio>
      ) : (
        <>
          <AspectRatio ratio={ratio}>
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <span className="loading loading-spinner"></span>
              </div>
            )}
            {hasError && (
              <div className="absolute inset-0 z-10 flex items-center justify-center text-destructive">
                {t("common.imageError")}
              </div>
            )}
            <Link
              to={fullPath}
              target="_blank"
              download
              className="group relative block h-full w-full"
            >
              <img
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                src={fullPath}
                alt={alt || label}
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  setHasError(true);
                }}
              />

              {overlay && (
                <>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                    <ExternalLink className="size-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {label && (
                    <div className="absolute bottom-0 left-0 right-0 text-xs text-white p-1 bg-black/50 text-center truncate">
                      {label}
                    </div>
                  )}
                </>
              )}
            </Link>
          </AspectRatio>
        </>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      {tooltipText ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{imageContent}</TooltipTrigger>
            <TooltipContent>
              <p>{tooltipText || t("common.clickToView")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        imageContent
      )}

      {showLabel && label && !overlay && (
        <span className="text-sm font-medium">{label}</span>
      )}
    </div>
  );
};

export default ImageViewer;
